import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dashApi, type CustomerRow } from "@/lib/dashApi";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel } from "@/components/dash/ui";

function RankBoard({ title, rows, metric }: { title: string; rows: CustomerRow[]; metric: (c: CustomerRow) => string }) {
  return (
    <Panel title={title}>
      {!rows.length ? <EmptyState compact>No customers yet.</EmptyState> : (
        <ol className="space-y-1">
          {rows.map((c, i) => (
            <li key={c.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-ink-700/60">
              <span className="w-5 text-center text-xs font-semibold text-slate-500">{i + 1}</span>
              <Link to={`/customers/${c.id}`} className="flex-1 truncate text-sm text-slate-200 hover:text-brand-300">
                {c.name || "—"}
                <span className="ml-2 text-[11px] text-slate-600">{c.email ?? ""}</span>
              </Link>
              <span className="tabular-nums text-sm font-medium text-white">{metric(c)}</span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

export default function CustomerRankingsPage() {
  const q = useQuery({ queryKey: ["customer-rankings"], queryFn: dashApi.customerRankings });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Rankings"
        description="Top customers by value, activity and breadth of services. Profit ranking is approximate — see the note below."
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : q.data && (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <RankBoard title="Highest value (total spend)" rows={q.data.highest_value} metric={(c) => fmtCurrency(c.total_spend)} />
            <RankBoard title="Most active (transactions + interactions)" rows={q.data.most_active}
              metric={(c) => `${fmtNumber(c.repair_count + c.order_count + c.invoice_count + c.support_count)} events`} />
            <RankBoard title="Multi-service customers" rows={q.data.multi_service} metric={(c) => `${c.services_used} services`} />
            <RankBoard title="Highest estimated LTV" rows={q.data.highest_ltv} metric={(c) => fmtCurrency(c.ltv_estimate)} />
          </div>
          <Panel title="On profitability">
            <p className="text-sm text-slate-400">{q.data.profit_note}</p>
          </Panel>
        </>
      )}
    </div>
  );
}
